import { supabase } from "../supabaseClient";
import { MAX_CHECK_FIELDS, MAX_CUSTOM_TEXT_LENGTH } from "./checkFieldTypes";
import { clampFieldToBand, pageLayoutBandHeight } from "./checkGeometry";
import { defaultCheckFields } from "./checkDefaultLayouts";
import { normalizePrizeLineColumns } from "./checkPrizeLines";

const TABLE = "checkTemplates";

const sanitizeFields = (fields, pageLayout) => {
  const bandHeight = pageLayoutBandHeight(pageLayout);
  return (Array.isArray(fields) ? fields : [])
    .slice(0, MAX_CHECK_FIELDS)
    .map((field) => {
      const clamped = clampFieldToBand(field, bandHeight);
      if (clamped.fieldType === "customText") {
        clamped.customText = String(clamped.customText || "").slice(
          0,
          MAX_CUSTOM_TEXT_LENGTH
        );
      } else {
        clamped.customText = null;
      }
      if (clamped.fieldType === "prizeLines") {
        clamped.prizeLineColumns = normalizePrizeLineColumns(
          clamped.prizeLineColumns
        );
        clamped.overflow = "wrap";
      } else {
        clamped.prizeLineColumns = null;
      }
      return clamped;
    });
};

export const listCheckTemplates = async (organizationID) => {
  if (!organizationID) return [];
  const { data, error } = await supabase
    .from(TABLE)
    .select(
      "id, organizationID, name, pageLayout, offsetXInches, offsetYInches, updatedAt"
    )
    .eq("organizationID", organizationID)
    .eq("isDeleted", false)
    .order("name", { ascending: true });
  if (error) throw error;
  return data || [];
};

export const getCheckTemplate = async (templateId) => {
  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("id", templateId)
    .eq("isDeleted", false)
    .maybeSingle();
  if (error) throw error;
  return data;
};

export const createCheckTemplate = async ({
  organizationID,
  userId,
  name = "Check template",
  pageLayout = "voucher",
}) => {
  const payload = {
    organizationID,
    name: (name || "Check template").trim() || "Check template",
    pageLayout,
    pageWidthInches: 8.5,
    pageHeightInches: 11,
    bandHeightInches: pageLayoutBandHeight(pageLayout),
    offsetXInches: 0,
    offsetYInches: 0,
    fields: defaultCheckFields(pageLayout),
    createdBy: userId,
    updatedBy: userId,
  };
  const { data, error } = await supabase
    .from(TABLE)
    .insert(payload)
    .select("*")
    .single();
  if (error) throw error;
  return data;
};

export const saveCheckTemplate = async ({ template, userId }) => {
  const pageLayout = template.pageLayout === "threePerPage" ? "threePerPage" : "voucher";
  const { data, error } = await supabase
    .from(TABLE)
    .update({
      name: (template.name || "Check template").trim() || "Check template",
      pageLayout,
      pageWidthInches: 8.5,
      pageHeightInches: 11,
      bandHeightInches: pageLayoutBandHeight(pageLayout),
      offsetXInches: Number(template.offsetXInches) || 0,
      offsetYInches: Number(template.offsetYInches) || 0,
      fields: sanitizeFields(template.fields, pageLayout),
      updatedBy: userId,
      updatedAt: new Date().toISOString(),
    })
    .eq("id", template.id)
    .eq("isDeleted", false)
    .select("*")
    .single();
  if (error) throw error;
  return data;
};

export const deleteCheckTemplate = async ({ templateId, userId }) => {
  const { error } = await supabase
    .from(TABLE)
    .update({
      isDeleted: true,
      deletedAt: new Date().toISOString(),
      deletedBy: userId,
      updatedBy: userId,
      updatedAt: new Date().toISOString(),
    })
    .eq("id", templateId);
  if (error) throw error;
};
